// 🌿 API Craft Back Office - Model CRUD Page
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useModels } from '../hooks/useModels';
import { useCrud } from '../hooks/useCrud';
import { usePageTitle } from '../hooks/usePageTitle';
import Layout from '../components/Layout';
import ForeignKeySelectAdvanced from '../components/ForeignKeySelectAdvanced';
import ForeignKeyDisplay from '../components/ForeignKeyDisplay';
import { DynamicRecord, DbModel } from '../types';

const ModelCrudPage: React.FC = () => {
  const { modelName } = useParams<{ modelName: string }>();
  const navigate = useNavigate();
  const { models } = useModels();
  const { 
    records, 
    isLoading, 
    error, 
    pagination, 
    createRecord, 
    updateRecord,
    deleteRecord,
    changePage,
  } = useCrud(modelName || '');

  const [selectedRecord, setSelectedRecord] = useState<DynamicRecord | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [editData, setEditData] = useState<{ [key: string]: any }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const currentModel: DbModel | undefined = models?.find(m => m.model === modelName);
  
  // Set dynamic page title
  usePageTitle(modelName ? `${modelName} Records` : 'Model Records');

  const handleCreateRecord = async () => {
    setIsSubmitting(true);
    try {
      await createRecord(formData);
      setShowCreateModal(false);
      setFormData({});
    } catch (error) {
      console.error('Failed to create record:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRecord = async () => {
    if (!selectedRecord) return;
    
    setIsSubmitting(true);
    try {
      await deleteRecord(selectedRecord.id);
      setShowDeleteModal(false);
      setSelectedRecord(null);
    } catch (error) {
      console.error('Failed to delete record:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowDetails = (record: DynamicRecord) => {
    setSelectedRecord(record);
    setEditData({ ...record });
    setIsEditing(false);
    setShowDetailsModal(true);
  };

  const handleUpdateRecord = async () => {
    if (!selectedRecord) return;
    
    setIsSubmitting(true);
    try {
      await updateRecord(selectedRecord.id, editData);
      setShowDetailsModal(false);
      setSelectedRecord(null);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update record:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentModel) {
    return (
      <Layout>
        <div className="text-center py-16 animation-scale-in">
          <div className="w-24 h-24 mx-auto bg-gradient-to-r from-red-100 to-red-200 rounded-2xl flex items-center justify-center mb-6 shadow-soft">
            <span className="text-4xl">❌</span>
          </div>
          <h3 className="text-2xl font-bold text-forest-800 mb-3">Model not found</h3>
          <p className="text-sage-600 mb-6">
            The model "{modelName}" could not be found.
          </p>
          <button onClick={() => navigate('/models')} className="btn-primary">
            <span className="mr-2">←</span>
            Back to Models
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animation-fade-in">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/models')}
              className="text-sage-600 hover:text-forest-700 transition-colors duration-200 flex items-center"
            >
              <span className="mr-2">←</span>
              Back to Models
            </button>
            <div>
              <h1 className="text-3xl font-bold text-forest-800">{modelName} Records</h1>
              <p className="text-sage-700">
                Manage data for your {modelName} model
              </p>
            </div>
          </div>
          
          <button 
            onClick={() => setShowCreateModal(true)} 
            className="btn-primary"
          >
            <span className="mr-2">✨</span>
            Add Record
          </button>
        </div>

        {error && (
          <div className="alert-error animation-scale-in">
            <span className="text-red-500 mr-3 text-xl">⚠️</span>
            <div>
              <h3 className="font-medium">Error loading records</h3>
              <p className="mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="card p-0 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-r from-mint-400 to-emerald-500 rounded-2xl flex items-center justify-center shadow-strong mb-4 animation-bounce-gentle">
                  <span className="text-2xl text-white">🌿</span>
                </div>
                <h3 className="text-lg font-semibold text-forest-800 mb-2">Loading Records</h3>
                <p className="text-sage-600">Please wait...</p>
              </div>
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto bg-gradient-to-r from-sage-100 to-mint-100 rounded-2xl flex items-center justify-center mb-6 shadow-soft">
                <span className="text-3xl">📝</span>
              </div>
              <h3 className="text-xl font-bold text-forest-800 mb-3">No records found</h3>
              <p className="text-sage-600 mb-6">
                Start by creating your first {modelName} record.
              </p>
              <button onClick={() => setShowCreateModal(true)} className="btn-primary">
                <span className="mr-2">✨</span>
                Create First Record
              </button>
            </div>
          ) : (
            <div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-sage-200">
                  <thead className="bg-gradient-to-r from-sage-50 to-mint-50">
                    <tr>
                      <th className="table-header">ID</th>
                      {currentModel.table_structure.fields.map(field => (
                        <th key={field.name} className="table-header">
                          {field.name}
                        </th>
                      ))}
                      <th className="table-header">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-sage-100">
                    {records.map((record) => (
                      <tr 
                        key={record.id} 
                        className="hover:bg-mint-50/50 transition-colors duration-200 cursor-pointer"
                        onClick={() => handleShowDetails(record)}
                      >
                        <td className="table-cell">
                          <code className="text-xs bg-sage-100 rounded px-2 py-1">
                            {record.id.substring(0, 8)}...
                          </code>
                        </td>
                        {currentModel.table_structure.fields.map(field => (
                          <td key={field.name} className="table-cell">
                            {field.type === 'foreign_key' ? (
                              <ForeignKeyDisplay 
                                field={field} 
                                value={record[field.name]} 
                              />
                            ) : (
                              String(record[field.name] || '—')
                            )}
                          </td>
                        ))}
                        <td className="table-cell">
                          <div className="flex space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowDetails(record);
                              }}
                              className="text-mint-600 hover:text-mint-800 transition-colors duration-200 text-sm font-medium"
                            >
                              View
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRecord(record);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-800 transition-colors duration-200 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination.total > pagination.pageSize && (
                <div className="bg-gradient-to-r from-sage-50 to-mint-50 px-6 py-4 flex items-center justify-between border-t border-sage-200">
                  <div>
                    <p className="text-sm text-sage-700">
                      Page {pagination.page} of {Math.ceil(pagination.total / pagination.pageSize)}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => changePage(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={() => changePage(pagination.page + 1)}
                      disabled={pagination.page >= Math.ceil(pagination.total / pagination.pageSize)}
                      className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create Record Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-sage-200">
                <h3 className="text-xl font-semibold text-forest-800">Create New {modelName}</h3>
                <p className="text-sage-600 text-sm mt-1">Fill in the details to create a new record</p>
              </div>
              
              <div className="p-6 space-y-4">
                {currentModel.table_structure.fields.map(field => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-forest-700 mb-2">
                      {field.name}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {field.type === 'boolean' ? (
                      <select
                        value={formData[field.name] || 'false'}
                        onChange={(e) => setFormData({...formData, [field.name]: e.target.value === 'true'})}
                        className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                      >
                        <option value="false">False</option>
                        <option value="true">True</option>
                      </select>
                    ) : field.type === 'integer' ? (
                      <input
                        type="number"
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({...formData, [field.name]: parseInt(e.target.value) || null})}
                        placeholder={`Enter ${field.name}`}
                        className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                        required={field.required}
                      />
                    ) : field.type === 'float' ? (
                      <input
                        type="number"
                        step="0.01"
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({...formData, [field.name]: parseFloat(e.target.value) || null})}
                        placeholder={`Enter ${field.name}`}
                        className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                        required={field.required}
                      />
                    ) : field.type === 'date' ? (
                      <input
                        type="date"
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                        className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                        required={field.required}
                      />
                    ) : field.type === 'datetime' ? (
                      <input
                        type="datetime-local"
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                        className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                        required={field.required}
                      />
                    ) : field.type === 'foreign_key' ? (
                      <ForeignKeySelectAdvanced
                        field={field}
                        value={formData[field.name]}
                        onChange={(value) => setFormData({...formData, [field.name]: value})}
                        required={field.required}
                      />
                    ) : (
                      <input
                        type={field.type === 'email' ? 'email' : 'text'}
                        value={formData[field.name] || ''}
                        onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                        placeholder={`Enter ${field.name}`}
                        maxLength={field.max_length}
                        className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                        required={field.required}
                      />
                    )}
                    
                    {field.description && (
                      <p className="text-xs text-sage-500 mt-1">{field.description}</p>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="p-6 border-t border-sage-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({});
                  }}
                  className="px-6 py-3 text-sage-600 hover:text-forest-700 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRecord}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gradient-to-r from-mint-500 to-emerald-600 text-white rounded-lg hover:from-mint-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? 'Creating...' : 'Create Record'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Details/Edit Record Modal */}
        {showDetailsModal && selectedRecord && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-sage-200 bg-gradient-to-r from-mint-50 to-sage-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-forest-800">
                      {isEditing ? 'Edit' : 'View'} {modelName} Record
                    </h3>
                    <p className="text-sage-600 text-sm mt-1">
                      {isEditing ? 'Make changes to the record' : 'Complete record details'}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-4 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 transition-colors text-sm"
                        disabled={isSubmitting}
                      >
                        ✏️ Edit
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-4 py-2 text-sage-600 hover:text-forest-700 transition-colors text-sm"
                        disabled={isSubmitting}
                      >
                        👁️ View Mode
                      </button>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {/* Record ID */}
                <div className="mb-6 p-4 bg-sage-50 rounded-lg border border-sage-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-medium text-forest-700">Record ID</h4>
                      <p className="text-sage-600 text-sm mt-1 font-mono">{selectedRecord.id}</p>
                    </div>
                    <div className="text-right">
                      <h4 className="text-sm font-medium text-forest-700">Created</h4>
                      <p className="text-sage-600 text-sm mt-1">
                        {new Date(selectedRecord.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Editable Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {currentModel.table_structure.fields.map(field => (
                    <div key={field.name} className="space-y-2">
                      <label className="block text-sm font-medium text-forest-700">
                        {field.name}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                        <span className="text-xs text-sage-500 ml-2">({field.type})</span>
                      </label>
                      
                      {!isEditing ? (
                        // View Mode
                        <div className="w-full px-4 py-3 border border-sage-200 rounded-lg bg-sage-50 text-sage-700">
                          {field.type === 'foreign_key' ? (
                            <ForeignKeyDisplay 
                              field={field} 
                              value={selectedRecord[field.name]} 
                            />
                          ) : field.type === 'boolean' ? (
                            selectedRecord[field.name] ? '✅ True' : '❌ False'
                          ) : (
                            String(selectedRecord[field.name] || '—')
                          )}
                        </div>
                      ) : (
                        // Edit Mode
                        <>
                          {field.type === 'boolean' ? (
                            <select
                              value={editData[field.name] || 'false'}
                              onChange={(e) => setEditData({...editData, [field.name]: e.target.value === 'true'})}
                              className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                            >
                              <option value="false">False</option>
                              <option value="true">True</option>
                            </select>
                          ) : field.type === 'integer' ? (
                            <input
                              type="number"
                              value={editData[field.name] || ''}
                              onChange={(e) => setEditData({...editData, [field.name]: parseInt(e.target.value) || null})}
                              className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                            />
                          ) : field.type === 'float' ? (
                            <input
                              type="number"
                              step="0.01"
                              value={editData[field.name] || ''}
                              onChange={(e) => setEditData({...editData, [field.name]: parseFloat(e.target.value) || null})}
                              className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                            />
                          ) : field.type === 'date' ? (
                            <input
                              type="date"
                              value={editData[field.name] || ''}
                              onChange={(e) => setEditData({...editData, [field.name]: e.target.value})}
                              className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                            />
                          ) : field.type === 'datetime' ? (
                            <input
                              type="datetime-local"
                              value={editData[field.name] || ''}
                              onChange={(e) => setEditData({...editData, [field.name]: e.target.value})}
                              className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                            />
                          ) : field.type === 'foreign_key' ? (
                            <ForeignKeySelectAdvanced
                              field={field}
                              value={editData[field.name]}
                              onChange={(value) => setEditData({...editData, [field.name]: value})}
                            />
                          ) : (
                            <input
                              type={field.type === 'email' ? 'email' : 'text'}
                              value={editData[field.name] || ''}
                              onChange={(e) => setEditData({...editData, [field.name]: e.target.value})}
                              maxLength={field.max_length}
                              className="w-full px-4 py-3 border border-sage-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-mint-500"
                            />
                          )}
                        </>
                      )}
                      
                      {field.description && (
                        <p className="text-xs text-sage-500">{field.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-6 border-t border-sage-200 bg-sage-50 flex justify-between">
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      setSelectedRecord(null);
                      setIsEditing(false);
                    }}
                    className="px-6 py-3 text-sage-600 hover:text-forest-700 transition-colors"
                    disabled={isSubmitting}
                  >
                    Close
                  </button>
                </div>
                
                {isEditing && (
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setEditData({ ...selectedRecord });
                        setIsEditing(false);
                      }}
                      className="px-6 py-3 text-sage-600 hover:text-forest-700 transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel Changes
                    </button>
                    <button
                      onClick={handleUpdateRecord}
                      disabled={isSubmitting}
                      className="px-6 py-3 bg-gradient-to-r from-mint-500 to-emerald-600 text-white rounded-lg hover:from-mint-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Record Modal */}
        {showDeleteModal && selectedRecord && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b border-sage-200">
                <h3 className="text-xl font-semibold text-forest-800">Delete Record</h3>
                <p className="text-sage-600 text-sm mt-1">This action cannot be undone</p>
              </div>
              
              <div className="p-6">
                <p className="text-sage-700">
                  Are you sure you want to delete this {modelName} record?
                </p>
                <div className="mt-4 p-3 bg-sage-50 rounded-lg">
                  <p className="text-sm text-sage-600">
                    <strong>ID:</strong> {selectedRecord.id}
                  </p>
                </div>
              </div>
              
              <div className="p-6 border-t border-sage-200 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedRecord(null);
                  }}
                  className="px-6 py-3 text-sage-600 hover:text-forest-700 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRecord}
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isSubmitting ? 'Deleting...' : 'Delete Record'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ModelCrudPage;
